import { Box, ButtonBase, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useShowInHomeLockToggle } from "@src/appV2/ExperimentalHomeHealth/lib/useShowInHomeLockToggle";
import { useWorker } from "@src/appV2/Worker/api/useWorker";

import { useIsMobile } from "../AppLayout/useResponsive";
import { Avatar } from "../components/Avatar";
import { IconButton } from "../components/IconButton";
import { Text } from "../components/Text";
import { ChatHeaderActions } from "./components/ChatHeaderActions";
import { useViewProfileHandler } from "./hooks/useViewProfileHandler";
import { InHomeLockToggle } from "./InHomeLock/components/InHomeLockToggle";
import { useTu } from "@src/appV2/i18n/useTu";

export interface ChatHeaderProps {
  workerId: string;
  workerName: string;
  onClose: () => void;
  onViewProfile?: (workerId: string) => void;
  showViewProfile?: boolean;
  onMarkConversationAsUnread?: () => void;
}

/**
 * Header component for the chat panel.
 * Displays worker avatar, name, qualification, and action buttons.
 */
export function ChatHeader(props: ChatHeaderProps) {
  const { tu } = useTu("chat");
  const {
    workerId,
    workerName,
    onClose,
    onViewProfile,
    showViewProfile,
    onMarkConversationAsUnread,
  } = props;

  // Fetch worker details for avatar and qualification
  const { data: worker } = useWorker(workerId);

  const session = useSession();
  const workplaceId = session.userId;
  const showLockToggle = useShowInHomeLockToggle();
  const isMobile = useIsMobile();
  const buttonSize = isMobile ? "medium" : "small";
  const handleViewProfile = useViewProfileHandler({ workerId, onViewProfile });
  const profileLinkEnabled = showViewProfile !== false;

  const avatar = (
    <Avatar size="medium" src={worker?.profileImageUrl ?? undefined} alt={workerName} />
  );
  const nameText = (
    <Text
      inline
      semibold
      variant="h6"
      className="worker-name"
      sx={(theme) => ({ color: theme.palette.base?.base })}
    >
      {workerName}
    </Text>
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 3,
      }}
    >
      {/* Left: Worker info */}
      <Stack direction="row" alignItems="center" gap={2}>
        {profileLinkEnabled ? (
          <ButtonBase
            aria-label={`View ${workerName}'s profile`}
            sx={(theme) => ({
              borderRadius: "50%",
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.base?.primary}`,
                outlineOffset: 2,
              },
            })}
            onClick={handleViewProfile}
          >
            {avatar}
          </ButtonBase>
        ) : (
          avatar
        )}
        <Stack direction="column" gap={1} sx={{ minWidth: 0 }}>
          {profileLinkEnabled ? (
            <ButtonBase
              sx={(theme) => ({
                alignSelf: "flex-start",
                borderRadius: theme.borderRadius?.small ?? theme.spacing(1),
                "&:hover .worker-name": { textDecoration: "underline" },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.base?.primary}`,
                  outlineOffset: 2,
                },
              })}
              onClick={handleViewProfile}
            >
              {nameText}
            </ButtonBase>
          ) : (
            nameText
          )}
          {worker?.qualification && (
            <Text
              variant="caption"
              sx={(theme) => ({
                color: theme.palette.base?.inactive,
              })}
            >
              {worker.qualification}
            </Text>
          )}
        </Stack>
      </Stack>

      {/* Right: Actions */}
      <Stack direction="row" alignItems="center" gap={2}>
        {showLockToggle && (
          <InHomeLockToggle workerId={workerId} workplaceId={workplaceId} size={buttonSize} />
        )}
        <ChatHeaderActions
          workerId={workerId}
          showViewProfile={showViewProfile}
          size={buttonSize}
          onViewProfile={onViewProfile}
          onMarkConversationAsUnread={onMarkConversationAsUnread}
        />
        <IconButton
          circular
          aria-label={tu("Close chat")}
          iconType="close"
          size={buttonSize}
          variant="outlined"
          onClick={onClose}
        />
      </Stack>
    </Box>
  );
}
